'use client';

import { TypographyP2, TypographyPBold } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { AnimatePresence, useReducedMotion } from 'motion/react';
import * as m from 'motion/react-m';
import { useEffect, useRef, useState } from 'react';

import { InlineMarkdown } from '../mic-test-preview/sharedComponents';
import type { EnhancedLine } from '../mic-test-preview/types';

// How long an added line keeps its forest highlight before settling to normal.
const HIGHLIGHT_HOLD_MS = 1100;

/**
 * Stable per-line key that survives add/remove across detail-level changes.
 * Dedupes repeated text (e.g. two identical bullets) by occurrence so each
 * line keeps its own AnimatePresence identity.
 */
function bodyKeys(body: EnhancedLine[]): string[] {
  const seen = new Map<string, number>();
  return body.map((line) => {
    const n = seen.get(line.text) ?? 0;
    seen.set(line.text, n + 1);
    return `${line.type}:${line.text}#${n}`;
  });
}

function BodyLine({ line, highlighted }: { line: EnhancedLine; highlighted: boolean }) {
  if (line.type === 'header') {
    return (
      <TypographyPBold
        className={cn(
          'mt-2 rounded transition-colors duration-500 first:mt-0',
          highlighted && 'bg-accent-forest/20 px-0.5 text-accent-forest'
        )}
      >
        {line.text}
      </TypographyPBold>
    );
  }
  const isBullet = line.type === 'bullet';
  return (
    <TypographyP2
      className={cn(
        'rounded text-text-secondary transition-colors duration-500',
        highlighted && 'bg-accent-forest/20 px-0.5 text-accent-forest'
      )}
    >
      {isBullet ? <>&bull; </> : null}
      <InlineMarkdown text={line.text} />
    </TypographyP2>
  );
}

/**
 * Renders a Clinical Note section body for the flag-ON Learn-the-basics detail
 * step. When the detail level changes (Low / Medium / High → a client-side
 * `applyDetailLevel` transform that adds or removes whole bullets), the delta
 * is highlighted in the same visual language as the legacy abbreviation swap:
 * added lines fade/scale in with a forest highlight that settles to normal
 * after a short hold, and removed lines fade out before leaving.
 *
 * The highlight only fires on an actual change to an already-populated note —
 * never on first render or when assets arrive late — and is suppressed under
 * `prefers-reduced-motion` (content just swaps).
 */
export function DetailNoteBody({ body }: { body: EnhancedLine[] }) {
  const prefersReducedMotion = useReducedMotion();
  const keys = bodyKeys(body);
  const signature = keys.join('|');

  // Previously-rendered line keys, so a detail-level change can highlight only
  // the added lines. Null until the first populated render so the initial note
  // (and a late asset population) never highlights.
  const prevKeysRef = useRef<Set<string> | null>(null);
  const [highlighted, setHighlighted] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const prev = prevKeysRef.current;
    const current = new Set(keys);
    prevKeysRef.current = current;
    // Only highlight when transforming an already-populated note (a real
    // user-initiated detail toggle), and never under reduced motion.
    if (!prev || prev.size === 0 || prefersReducedMotion) return undefined;

    const added = keys.filter((k) => !prev.has(k));
    const removed = [...prev].filter((k) => !current.has(k));
    // Adding detail (e.g. → High): the new lines glow as they enter. Removing
    // detail (e.g. → Low): the new lines are gone, so glow the REMAINING lines
    // of this changed section so the removal still reads as a deliberate change
    // (alongside the removed lines' exit fade).
    const toHighlight = added.length > 0 ? added : removed.length > 0 ? keys : [];
    if (toHighlight.length === 0) return undefined;

    setHighlighted(new Set(toHighlight));
    const id = setTimeout(() => setHighlighted(new Set()), HIGHLIGHT_HOLD_MS);
    return () => clearTimeout(id);
    // `signature` is the value-identity of `keys`; it changes exactly when the
    // set of lines changes, which is the only thing we react to here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, prefersReducedMotion]);

  if (body.length === 0) return null;

  // Reduced motion: plain swap — no enter/exit motion, no highlight.
  if (prefersReducedMotion) {
    return (
      <div className="flex flex-col gap-1 py-1">
        {body.map((line, i) => (
          <BodyLine key={keys[i]} line={line} highlighted={false} />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1 py-1">
      <AnimatePresence initial={false} mode="popLayout">
        {body.map((line, i) => {
          const key = keys[i]!;
          return (
            <m.div
              key={key}
              layout
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.28, ease: 'easeOut' }}
            >
              <BodyLine line={line} highlighted={highlighted.has(key)} />
            </m.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
